import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { fetchGrades } from "../../academicOperations/services/gradesService";
import { Grade } from "../../academicOperations/types";
import { AlertRecipients, ClassRef } from "../types";

type Props = {
  value: AlertRecipients;
  onChange: (value: AlertRecipients) => void;
  /** "All classes" is offered; otherwise at least one class must be picked. */
  allowAll: boolean;
  /** Matches the other top-bar controls. */
  buttonSx?: object;
};

const keyOf = (c: ClassRef) => `${c.grade}|${c.section}`;

/** "All classes" | "Five, Six-A (3 classes)" | "Select classes" */
export const describeRecipients = (value: AlertRecipients, grades: Grade[]): string => {
  if (value === "ALL") return "All classes";
  if (value.length === 0) return "Select classes";
  const picked = new Set(value.map(keyOf));
  const parts = grades.flatMap((grade) => {
    const sections = grade.sections.filter((s) => picked.has(keyOf({ grade: grade.code, section: s.code })));
    if (sections.length === 0) return [];
    // a whole grade reads as just its name
    return sections.length === grade.sections.length ? [grade.name] : sections.map((s) => `${grade.name}-${s.name}`);
  });
  return `${parts.join(", ")} (${value.length} class${value.length === 1 ? "" : "es"})`;
};

/**
 * "To" picker for Send Alert: All classes, or any mix of grades / sections. Grades and their sections
 * come from GET /grades - exactly the sections linked to each grade (active grade_section rows);
 * grades without sections are left out. Desktop: a popover; phone: full screen.
 */
const ClassPicker: React.FC<Props> = ({ value, onChange, allowAll, buttonSx }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    fetchGrades()
      .then((all) => setGrades(all.filter((g) => g.sections.length > 0)))
      .catch(() => setError("Failed to load classes."))
      .finally(() => setLoading(false));
  }, []);

  const picked = useMemo(() => new Set(value === "ALL" ? [] : value.map(keyOf)), [value]);
  const isAll = value === "ALL";

  const setPicked = (next: Set<string>) =>
    onChange(
      Array.from(next).map((k) => {
        const [grade, section] = k.split("|");
        return { grade, section };
      })
    );

  /** Ticks or unticks all of `keys`: all ticked -> untick them, otherwise tick them. */
  const toggle = (keys: string[]) => {
    const next = new Set(picked);
    const allTicked = keys.every((k) => next.has(k));
    keys.forEach((k) => (allTicked ? next.delete(k) : next.add(k)));
    setPicked(next);
  };

  const label = loading ? "Loading…" : describeRecipients(value, grades);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);

  const list = (
    <Box sx={{ minWidth: 280, maxHeight: fullScreen ? "none" : 420, overflowY: "auto", py: 1 }}>
      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
      {loading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={22} />
        </Box>
      )}
      {allowAll && (
        <>
          <FormControlLabel
            sx={{ px: 2, width: "100%" }}
            control={<Checkbox checked={isAll} onChange={() => onChange(isAll ? [] : "ALL")} />}
            label={<Typography fontWeight={600}>All classes</Typography>}
          />
          <Divider />
        </>
      )}
      {!loading && grades.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
          No classes are set up yet (Academics → Setup → Grades).
        </Typography>
      )}
      {grades.map((grade) => {
        const keys = grade.sections.map((s) => keyOf({ grade: grade.code, section: s.code }));
        const count = isAll ? keys.length : keys.filter((k) => picked.has(k)).length;
        return (
          <Box key={grade.id} display="flex" alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 0.25 }}>
            <FormControlLabel
              sx={{ minWidth: 110, mr: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={count === keys.length}
                  indeterminate={count > 0 && count < keys.length}
                  disabled={isAll}
                  onChange={() => toggle(keys)}
                />
              }
              label={<Typography fontWeight={600}>{grade.name}</Typography>}
            />
            {grade.sections.map((section) => {
              const key = keyOf({ grade: grade.code, section: section.code });
              return (
                <FormControlLabel
                  key={key}
                  sx={{ mr: 1 }}
                  control={<Checkbox size="small" checked={isAll || picked.has(key)} disabled={isAll} onChange={() => toggle([key])} />}
                  label={section.name}
                />
              );
            })}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color={value !== "ALL" && value.length === 0 ? "warning" : "primary"}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ ...buttonSx, maxWidth: 320, justifyContent: "space-between", textTransform: "none" }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          To: {label}
        </Box>
      </Button>

      {fullScreen ? (
        <Dialog open={open} onClose={close} fullScreen>
          <DialogTitle>Send to</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {list}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={close} fullWidth>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.5 } } }}
        >
          {list}
        </Popover>
      )}
    </>
  );
};

export default ClassPicker;
